const DESKTOP_QUERY = '(min-width: 750px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const VARIABLE_SPEED_MIN = 20;
const VARIABLE_SPEED_MAX = 120;

class DarkrimTypeHero extends HTMLElement {
  #config = {};
  #phrases = [];
  #contentElement = null;
  #cursorElement = null;
  #timer = null;
  #observer = null;
  #desktopMediaQuery = window.matchMedia(DESKTOP_QUERY);
  #reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
  #currentVideoViewport = null;
  #phraseIndex = 0;
  #characterIndex = 0;
  #displayedCharacters = [];
  #isDeleting = false;
  #hasStarted = false;

  connectedCallback() {
    this.#readConfig();
    this.#contentElement = this.querySelector('[data-darkrim-type-content]');
    this.#cursorElement = this.querySelector('[data-darkrim-type-cursor]');

    this.#desktopMediaQuery.addEventListener('change', this.#handleViewportChange);
    this.#reducedMotionQuery.addEventListener('change', this.#handleMotionPreferenceChange);
    this.#syncVideo();
    this.#configureCursor();

    if (!this.#contentElement || this.#phrases.length === 0) return;

    if (this.#reducedMotionQuery.matches) {
      this.#renderStaticPhrase();
      return;
    }

    this.#contentElement.textContent = '';
    this.#observeVisibility();
  }

  disconnectedCallback() {
    this.#stopTyping();
    this.#observer?.disconnect();
    this.#desktopMediaQuery.removeEventListener('change', this.#handleViewportChange);
    this.#reducedMotionQuery.removeEventListener('change', this.#handleMotionPreferenceChange);
  }

  #readConfig() {
    const configElement = this.querySelector('[data-darkrim-type-config]');

    try {
      this.#config = JSON.parse(configElement?.textContent || '{}');
    } catch (error) {
      console.warn('DARKRIM Type Hero: invalid section configuration.', error);
      this.#config = {};
    }

    this.#phrases = String(this.#config.text || '')
      .split(/\r?\n/)
      .map((phrase) => phrase.trim())
      .filter(Boolean);
  }

  #configureCursor() {
    if (!this.#cursorElement) return;

    this.#cursorElement.textContent = this.#config.cursorCharacter || '|';
    this.#cursorElement.hidden = this.#config.showCursor === false;
    this.#cursorElement.style.setProperty(
      '--darkrim-type-cursor-duration',
      `${Number(this.#config.cursorBlinkDuration) || 500}ms`,
    );
  }

  #observeVisibility() {
    if (!('IntersectionObserver' in window)) {
      this.#startTyping();
      return;
    }

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        this.#observer?.disconnect();
        this.#startTyping();
      },
      { threshold: 0.1 },
    );
    this.#observer.observe(this);
  }

  #startTyping() {
    if (this.#hasStarted || this.#reducedMotionQuery.matches) return;
    this.#hasStarted = true;
    this.#tick();
  }

  #stopTyping() {
    if (this.#timer) window.clearTimeout(this.#timer);
    this.#timer = null;
    this.#hasStarted = false;
  }

  #resetTypingState() {
    this.#phraseIndex = 0;
    this.#characterIndex = 0;
    this.#displayedCharacters = [];
    this.#isDeleting = false;
    this.#contentElement.textContent = '';
    this.#cursorElement?.classList.remove('darkrim-type-hero__cursor--typing-hidden');
  }

  #renderStaticPhrase() {
    this.#stopTyping();
    this.#observer?.disconnect();
    this.#resetTypingState();
    this.#contentElement.textContent = this.#phrases[0] || '';
  }

  #tick() {
    const phrase = this.#phrases[this.#phraseIndex] || '';
    const characters = Array.from(phrase);
    const isActivelyTyping = this.#isDeleting || this.#characterIndex < characters.length;

    this.#cursorElement?.classList.toggle(
      'darkrim-type-hero__cursor--typing-hidden',
      Boolean(this.#config.hideCursorWhileTyping && isActivelyTyping),
    );

    if (this.#isDeleting) {
      if (this.#displayedCharacters.length === 0) {
        this.#isDeleting = false;

        if (this.#phraseIndex === this.#phrases.length - 1 && this.#config.loop === false) {
          this.#cursorElement?.classList.remove('darkrim-type-hero__cursor--typing-hidden');
          return;
        }

        this.#phraseIndex = (this.#phraseIndex + 1) % this.#phrases.length;
        this.#characterIndex = 0;
        this.#timer = window.setTimeout(() => this.#tick(), 200);
        return;
      }

      this.#displayedCharacters.pop();
      this.#characterIndex = this.#displayedCharacters.length;
      this.#contentElement.textContent = this.#displayedCharacters.join('');
      this.#timer = window.setTimeout(
        () => this.#tick(),
        Number(this.#config.deletingSpeed) || 30,
      );
      return;
    }

    if (this.#characterIndex < characters.length) {
      this.#displayedCharacters.push(characters[this.#characterIndex]);
      this.#characterIndex += 1;
      this.#contentElement.textContent = this.#displayedCharacters.join('');
      this.#timer = window.setTimeout(() => this.#tick(), this.#getTypingSpeed());
      return;
    }

    if (this.#config.loop === false && this.#phraseIndex === this.#phrases.length - 1) {
      this.#cursorElement?.classList.remove('darkrim-type-hero__cursor--typing-hidden');
      return;
    }

    this.#timer = window.setTimeout(() => {
      this.#isDeleting = true;
      this.#tick();
    }, Number(this.#config.pauseDuration) || 2000);
  }

  #getTypingSpeed() {
    if (this.#config.variableSpeed) {
      return Math.floor(Math.random() * (VARIABLE_SPEED_MAX - VARIABLE_SPEED_MIN + 1)) + VARIABLE_SPEED_MIN;
    }

    return Number(this.#config.typingSpeed) || 50;
  }

  #syncVideo() {
    const viewport = this.#desktopMediaQuery.matches ? 'desktop' : 'mobile';
    const mode = this.dataset[`${viewport}Mode`];
    const host = this.querySelector('[data-darkrim-type-video-host]');

    if (!host) return;

    if (mode !== 'video') {
      host.replaceChildren();
      this.#currentVideoViewport = null;
      return;
    }

    if (this.#currentVideoViewport === viewport && host.firstElementChild) return;

    const template = this.querySelector(`[data-darkrim-type-video-template="${viewport}"]`);
    if (!(template instanceof HTMLTemplateElement)) return;

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector('video');

    if (video) {
      video.setAttribute('aria-hidden', 'true');
      video.tabIndex = -1;
      video.muted = true;
      video.playsInline = true;

      if (this.#reducedMotionQuery.matches) {
        video.autoplay = false;
        video.removeAttribute('autoplay');
      }
    }

    host.replaceChildren(fragment);
    this.#currentVideoViewport = viewport;

    if (video && !this.#reducedMotionQuery.matches) {
      video.play().catch(() => {
        // Autoplay can be denied by browser policy; the poster remains visible.
      });
    }
  }

  #handleViewportChange = () => {
    this.#syncVideo();
  };

  #handleMotionPreferenceChange = () => {
    this.#currentVideoViewport = null;
    this.#syncVideo();

    if (!this.#contentElement || this.#phrases.length === 0) return;

    if (this.#reducedMotionQuery.matches) {
      this.#renderStaticPhrase();
      return;
    }

    this.#resetTypingState();
    this.#observeVisibility();
  };
}

if (!customElements.get('darkrim-type-hero')) {
  customElements.define('darkrim-type-hero', DarkrimTypeHero);
}
